import {useMemo} from 'react';
import {createBrowserHistory} from 'history';
import ld from 'lodash';
import qs from 'qs';
import {KV} from 'utils/types';

import {hasWindow} from './functions';

const qsOptions = {
	allowDots: true,
	ignoreQueryPrefix: true,
};

const getObj = (prefix?: string): KV => {
	if (!hasWindow()) return {};

	const query = window.location.search;
	if (!query) {
		return {};
	}

	let obj = qs.parse(query, qsOptions) as KV;
	if (prefix) {
		obj = ld.get(obj, prefix, {}) as KV;
	}

	return ld.isObject(obj) ? obj: {};
};

const setObj = (obj: object) => {
	if (!hasWindow()) return;

	const query = window.location.search;
	const value = '?' + qs.stringify(obj, qsOptions);

	if (query === value) {
		return;
	}

	createBrowserHistory().replace({
		search: value,
	});

};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const flatKeys = (obj: {[key: string]: any}, keys: string[] = []) => {
	for (const k of Object.keys(obj)) {
		if (ld.isPlainObject(obj[k])) {
			flatKeys(obj[k] as KV).forEach((i) => keys.push(`${k}.${i}`));
		} else {
			keys.push(k);
		}
	}
	return keys;
};

const read = (prefix?: string) => (defaults: KV): KV => {
	const copy = ld.cloneDeep(defaults);
	const queryObj = getObj(prefix);

	for (const key of flatKeys(copy)) {
		const value = ld.get(queryObj, key);
		if (value) {
			ld.set(copy, key, value);
		}
	}

	return copy;
};

const update = (prefix?: string) => (value: KV, ref?: object): void => {
	let newValue = value;
	if (ref) {
		newValue = ld.pick(value,
			flatKeys(ref).filter(
				(key: string) => ld.get(value, key) !== ld.get(ref, key)
			),
		);
	}

	setObj(prefix ?
		ld.set(getObj(), prefix, newValue) :
		ld.merge(getObj(), newValue),
	);
};

export const useQueryValues = (prefix?: string) => (
	useMemo(() => ({
		read: read(prefix),
		update: update(prefix),
	}), [prefix])
);
